import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Awareness } from "y-protocols/awareness";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";

import { farbeFuer } from "../lib/weeklyLive/caret.js";
import { useCurrentUser } from "./useAuth.js";
import { weeklyKeys } from "./useWeekly.js";

/** So lange darf der erste Abgleich dauern, bevor das Weekly beim Speichern ohne Live-Verbindung bleibt. */
export const LIVE_VERBINDEN_MS = 6000;

/** Close-Codes des Servers (apps/api/src/modules/weekly/live/raum.ts). */
const VERALTET = 4409;
const ABGESCHLOSSEN = 4410;

export interface WeeklyLive {
  /** `verbinde` bis zum ersten Abgleich; `aus` = ohne Live-Verbindung, gespeichert wird wie bisher. */
  modus: "verbinde" | "live" | "aus";
  /** Gerade verbunden? Nach dem ersten Abgleich bleibt `modus` live, auch offline — Änderungen werden nachgereicht. */
  verbunden: boolean;
  doc: Y.Doc;
  anwesenheit: Awareness;
  /** Der Raum wurde neu aufgebaut und dieses Dokument verworfen (Offline-Getipptes ging verloren). */
  neuGeladen: boolean;
}

export interface LiveNutzer {
  employeeId: string;
  name: string;
  farbe: string;
}

/**
 * Live-Raum eines offenen Weeklies (#305). Kommt keine Verbindung zustande —
 * etwa weil der Proxy noch keine WebSockets durchreicht —, bleibt `modus`
 * „aus", und die Notizfelder speichern wie vorher über die REST-Routen.
 */
export function useWeeklyLive(weeklyId: string, aktiv: boolean): WeeklyLive | null {
  const qc = useQueryClient();
  const { user } = useCurrentUser();
  const [versuch, setVersuch] = useState(0);
  const [zustand, setZustand] = useState<WeeklyLive | null>(null);

  useEffect(() => {
    if (!aktiv || !user) {
      setZustand(null);
      return;
    }
    const doc = new Y.Doc();
    const basis = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/api/v1/weekly`;
    const provider = new WebsocketProvider(basis, `${weeklyId}/live`, doc, { maxBackoffTime: 5000 });
    const ich: LiveNutzer = { employeeId: user.employeeId, name: user.fullName, farbe: farbeFuer(user.employeeId) };
    provider.awareness.setLocalStateField("user", ich);
    let synchron = false;
    let aktuell: WeeklyLive = {
      modus: "verbinde", verbunden: false, doc, anwesenheit: provider.awareness, neuGeladen: versuch > 0,
    };
    const setze = (teil: Partial<WeeklyLive>) => {
      aktuell = { ...aktuell, ...teil };
      setZustand(aktuell);
    };
    setze({});

    const neuLaden = () => void qc.invalidateQueries({ queryKey: weeklyKeys.detail(weeklyId) });
    const meta = doc.getMap("meta");
    const beiMeta = (e: Y.YMapEvent<unknown>) => {
      if (e.keysChanged.has("stand") || e.keysChanged.has("closed")) neuLaden();
    };
    meta.observe(beiMeta);

    provider.on("sync", (ok: boolean) => {
      if (!ok) return;
      if (!synchron) {
        synchron = true;
        window.clearTimeout(frist);
        // Nach einem Abbruch meldet sich der Client mit seinem Raum; ein neu aufgebauter Raum antwortet 4409.
        const generation = meta.get("generation");
        if (typeof generation === "string") provider.params = { gen: generation };
        setze({ modus: "live" });
      }
    });
    provider.on("status", ({ status }: { status: string }) => setze({ verbunden: status === "connected" }));
    provider.on("closed", ({ code }: { code: number }) => {
      if (code === VERALTET) {
        setVersuch((v) => v + 1);
        neuLaden();
      } else if (code === ABGESCHLOSSEN) {
        neuLaden();
      } else if (!synchron) {
        setze({ modus: "aus" });
      }
    });
    const frist = window.setTimeout(() => {
      if (synchron) return;
      provider.disconnect();
      setze({ modus: "aus" });
    }, LIVE_VERBINDEN_MS);

    return () => {
      window.clearTimeout(frist);
      meta.unobserve(beiMeta);
      provider.destroy();
      doc.destroy();
    };
    // `user` nur über die Id: ein neues Objekt desselben Nutzers baut keinen neuen Raum.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyId, aktiv, user?.employeeId, versuch]);

  return zustand;
}
